import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import DataSources from "./Views";
import CommonLayout from "../../../Common/Layout";
import MultiSelectDropDown from "../../../Common/MultiSelectDropDown";
import { ButtonUI, ConfirmPopup } from "../../../Common";

import DataSourceClass from "../../../../service/dataSource";
import { setPrevState } from "../../../../util/common";
import { showToast } from "../../../../store/actions";

import { DATA_SOURCE, OBJECT_TYPE } from "../../../../util/Data/DataModeling";
import { DATASOURCE_TYPES, DATASOURCE_VIEWS } from "./data";
import { navDatasets } from "../../../kmk/AppData";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import MultiSelect from "../../../../util/multiselect";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    padding: "25px 25px 0 25px",
    display: "flex",
    flexWrap: "wrap",
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 4,
    display: "inline-flex",
  },
  selectElem: {
    width: "100%",
    minWidth: "180px",
    maxWidth: "180px",
  },
  iconBusinessRule: {
    marginLeft: "20px",
    backgroundColor: "#E1D4D4",
    "&:hover": {
      backgroundColor: "#facb9d",
    },
    "& img": {
      height: "16px",
      width: "16px",
    },
  },
}));

const DataSource = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { Workspace_id } = useParams();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState(1);
  const [dataSourceList, setDataSourceList] = useState([]);
  const [sources, setSources] = useState(false);
  const [mappedObjects, setMappedObjects] = useState(false);
  const [deleteState, setDeleteState] = useState({ open: false, ds: { name: "", id: "" } });
  const [selectedDSTypes, setSelectedDSTypes] = useState([]);

  const [tags, setTags] = useState({
    allTags: [],
    selectedTags: [],
  });

  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, accessRole, IsDataVaultEnabled } = workspace.workspace;

  const initialSources = useRef([]);
  const tagsRef = useRef(tags);

  const fetchDataForERDiagram = useCallback(async () => {
    setPageLoading(true);

    const dataSource = new DataSourceClass();
    const { success, data } = await dataSource.getAllObjects(Workspace_id, OBJECT_TYPE.DataSource);

    if (success) setSources(JSON.parse(data).sources);
    setPageLoading(false);
  }, [Workspace_id]);

  const fetchMappedObjects = useCallback(async () => {
    setPageLoading(true);

    const dataSource = new DataSourceClass();
    const { success, data } = await dataSource.getMappedObjects(Workspace_id);

    if (success) setMappedObjects(data);
    else setMappedObjects([]);

    setPageLoading(false);
  }, [Workspace_id]);

  const handleAddClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navDatasets.link}/add`);
  }, [Workspace_id, navigate]);

  const handleSearch = useCallback((e, sourceList = []) => {
    if (e.key === "Enter") {
      const searchVal = e.target.value;

      let filtered = [];
      sourceList.forEach((source) => {
        if (source.displayName.toLowerCase().includes(searchVal.trim().toLowerCase())) filtered.push(source);
      });

      setDataSourceList(filtered);
    }
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setDataSourceList(initialSources.current);
  }, []);

  const handleViewChange = useCallback(
    async (e) => {
      const { value } = e.target;
      setView(e.target.value);
      setSearchText("");
      setDataSourceList(initialSources.current);
      setSelectedDSTypes(DATASOURCE_TYPES.map(({ value }) => value));
      if (value === DATASOURCE_VIEWS["ER Diagram"] && !sources) await fetchDataForERDiagram();
      else if (value === DATASOURCE_VIEWS.Mapping && mappedObjects === false) await fetchMappedObjects();
    },
    [fetchDataForERDiagram, fetchMappedObjects, sources]
  );

  const getObjectsListing = useCallback(
    async (selectedTags = tagsRef.current?.selectedTags, searchVal = "") => {
      setLoading(true);

      const dataSource = new DataSourceClass();
      const { success, data = [] } = await dataSource.getObjectsListing(
        Workspace_id,
        OBJECT_TYPE.DataSource,
        selectedTags
      );

      if (success) {
        setDataLoaded(true);
        let filteredData = [...data];
        if (data && data.length > 0)
          filteredData = data.filter((source) =>
            source.displayName.toLowerCase().includes(searchVal.trim().toLowerCase())
          );

        setDataSourceList(filteredData);

        initialSources.current = data;
      }

      setLoading(false);
    },
    [Workspace_id]
  );

  const handleDeleteClick = useCallback((ds) => {
    setDeleteState((prev) => ({ open: !prev.open, ds }));
  }, []);

  const handleDelete = async () => {
    setPageLoading(true);

    const { success, message } = await new DataSourceClass().deleteDataSource(Workspace_id, deleteState.ds.id);
    if (success) {
      dispatch(
        showToast({
          message: message,
          type: "success",
        })
      );
      handleDeleteClick({ name: "", id: "" });
      setSearchText("");
      setPageLoading(false);
      await getObjectsListing();
    } else {
      handleDeleteClick({ name: "", id: "" });
      setPageLoading(false);
    }
  };

  const handleDSTypeChange = (selectionList) => {
    setSelectedDSTypes(selectionList);
  };

  const handleTagsChange = useCallback(
    (selectionList) => {
      tagsRef.current = { ...tagsRef.current, selectedTags: selectionList };
      setPrevState(setTags, { selectedTags: selectionList });
      getObjectsListing(selectionList, searchText);
    },
    [getObjectsListing, searchText]
  );

  useEffect(() => {
    const getTags = async () => {
      const dataSource = new DataSourceClass();
      const { data, success } = await dataSource.getTags(Workspace_id, OBJECT_TYPE.DataSource);
      if (success) {
        const tags = {
          allTags: data.map(({ TagKey }) => ({ Name: TagKey })),
          selectedTags: data.filter(({ isSelected }) => isSelected).map(({ TagKey }) => TagKey),
        };
        tagsRef.current = tags;
        setPrevState(setTags, tags);
      }
    };

    (async () => {
      setLoading(true);
      await getTags();
      getObjectsListing();
      setSelectedDSTypes(DATASOURCE_TYPES.map(({ value }) => value));
    })();

    return () => {};
  }, [Workspace_id, getObjectsListing]);

  const $viewTypes = useMemo(() => {
    const views = { ...DATASOURCE_VIEWS };
    if (!IsDataVaultEnabled) {
      delete views["ER Diagram"];
    }
    return views;
  }, [IsDataVaultEnabled]);

  const $filteredSources = useMemo(() => {
    return [
      ...dataSourceList.filter(({ datasetType, type }) => {
        if (["Table", "View"].includes(type) && datasetType === "readonly") return true;
        return selectedDSTypes.includes(type);
      }),
    ];
  }, [selectedDSTypes, dataSourceList]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      { link: "#", displayName: DATA_SOURCE.plural, active: true },
    ],
    [Workspace_id, workspaceName]
  );

  return (
    <CommonLayout title="Data Modeling" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: 99999 }} open={pageLoading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Toolbar className={classes.Header}>
          <Box sx={{ display: "flex", flex: "1", alignItems: "center", minWidth: "340px", paddingRight: "16px" }}>
            <Typography className={classes.HeaderTitle} component="div" gutterBottom sx={{ paddingRight: "16px" }}>
              {DATA_SOURCE.plural}
            </Typography>
            {[1, 2].includes(accessRole) && (
              <Box>
                <ButtonUI endIcon={<AddIcon />} variant="contained" color="info" onClick={() => handleAddClick()}>
                  {`Add ${DATA_SOURCE.singular}`}
                </ButtonUI>
              </Box>
            )}

            <Tooltip placement="top" arrow title="Business Rules">
              <Link to={`/my-workspaces/${Workspace_id}/bussiness-rules`} state={{ source: "data-sources" }}>
                <IconButton size="medium" className={classes.iconBusinessRule}>
                  <img src="/images/business-rules.png" alt="Business Rules" />
                </IconButton>
              </Link>
            </Tooltip>

            <HelpContextIcon screen={DATA_SOURCE.plural} />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "nowrap", flex: "1", flexDirection: "row-reverse" }}>
            <FormControl variant="standard" disabled={loading}>
              <Select label="View" value={view} onChange={handleViewChange} className={classes.selectElem}>
                {Object.keys($viewTypes).map((key) => (
                  <MenuItem key={$viewTypes[key]} value={$viewTypes[key]}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {view !== DATASOURCE_VIEWS.Mapping && (
              <MultiSelectDropDown
                dataid="dsTypeList"
                variant="standard"
                placeHolder={<MultiSelect title="Type" dataLength={selectedDSTypes.length} />}
                options={DATASOURCE_TYPES}
                selected={selectedDSTypes}
                isSelectAll={true}
                onSelectChange={(selectionList) => handleDSTypeChange(selectionList)}
                disabled={view === DATASOURCE_VIEWS["ER Diagram"] || loading}
              />
            )}
            {view === DATASOURCE_VIEWS["List View"] && (
              <MultiSelectDropDown
                dataid="dsTagsFilter"
                variant="standard"
                placeHolder={<MultiSelect title="Tags" dataLength={tags.selectedTags.length} />}
                options={tags.allTags}
                selected={tags.selectedTags}
                isSelectAll={false}
                disabled={loading}
                emptySelectionAllowed={true}
                onSelectChange={handleTagsChange}
                basedOn="Name"
                allowSearching
              />
            )}
            {view !== DATASOURCE_VIEWS.Mapping && (
              <TextField
                data-id="dsSearch"
                margin="normal"
                placeholder="Search"
                variant="standard"
                sx={{ m: 0, mr: "20px", minWidth: "180px", maxWidth: "180px" }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => handleSearch(e, initialSources.current)}
                disabled={view === DATASOURCE_VIEWS["ER Diagram"] || loading}
                InputProps={{
                  startAdornment: <SearchIcon color="primary" />,
                  ...(searchText && {
                    endAdornment: (
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon color="primary" />
                      </IconButton>
                    ),
                  }),
                }}
              />
            )}
          </Box>
        </Toolbar>
        <DataSources
          view={view}
          loading={loading}
          sources={$filteredSources}
          diagramSources={sources}
          mappedObjects={mappedObjects}
          selectedTags={tags.selectedTags}
          dataLoaded={dataLoaded}
          handleDeleteClick={handleDeleteClick}
        />
      </Box>
      {deleteState.open && (
        <ConfirmPopup
          configData={{
            popupTitle: `Delete ${DATA_SOURCE.plural}`,
            popupMessage: `Are you sure you want to delete “${deleteState.ds.name}”?`,
            handleCancel: () => handleDeleteClick({ name: "", id: "" }),
            handleClick: handleDelete,
            loadingForConfirm: false,
          }}
        />
      )}
    </CommonLayout>
  );
};

export default DataSource;
